<template>
  <section class="llm-chat-panel" role="region" aria-label="Metersuite AI chat" @click.stop>
    <header class="llm-chat-header">
      <div>
        <div class="llm-chat-title">Metersuite AI</div>
      </div>
      <button class="llm-icon-button" type="button" aria-label="Hide chat" title="Hide chat" @click="closePanel">
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="M4.5 4.5L15.5 15.5M15.5 4.5L4.5 15.5" />
        </svg>
      </button>
    </header>

    <div ref="messagesScroller" class="llm-chat-messages" aria-live="polite">
      <div v-if="!messages.length" class="llm-chat-empty">
        Puoi chiedere informazioni su dispositivi, punti di misura, letture, allarmi e notifiche, SIM, LoRa, DLMS, WBUS e OMS, oppure generare report. Ricorda che l’intelligenza artificiale è uno strumento di supporto: aiuta, ma non sostituisce il lavoro umano, e può avere un proprio stile di risposta e carattere.
      </div>
      <div
          v-for="message in messages"
          :key="message.id"
          :class="['llm-message-row', `llm-message-${message.role}`]"
      >
        <div class="llm-message-bubble">
          <div class="llm-message-text">{{ message.text }}</div>

          <div v-if="message.toolCalls?.length" class="llm-tool-list">
            <div
                v-for="(toolCall, index) in message.toolCalls"
                :key="`${message.id}-tool-${index}`"
                class="llm-tool-item"
            >
              <div :class="['llm-tool-chip', { failed: !toolCall.success }]">
                <span class="llm-tool-dot"></span>
                <span class="llm-tool-name">{{ formatToolName(toolCall.tool_name) }}</span>
                <span v-if="toolCall.metadata?.row_count" class="llm-tool-meta">
                  {{ toolCall.metadata.row_count }} rows
                </span>
                <button
                    v-if="findDownloadUrl(toolCall)"
                    class="llm-tool-download"
                    type="button"
                    @click.stop.prevent="downloadToolArtifact(toolCall)"
                >
                  CSV
                </button>
              </div>

              <div
                  v-if="shouldShowColumnPicker(message.toolCalls, index)"
                  class="llm-column-picker"
                  @click.stop
              >
                <div class="llm-column-picker-header">
                  <span>Scegli colonne</span>
                  <span v-if="selectedColumnsForTool(toolCall).length">
                    {{ selectedColumnsForTool(toolCall).length }} selezionate
                  </span>
                </div>
                <div class="llm-column-chip-list">
                  <button
                      v-for="column in columnOptionsForTool(toolCall)"
                      :key="`${artifactKeyForTool(toolCall)}-${column}`"
                      :class="['llm-column-chip', { selected: isColumnSelected(toolCall, column) }]"
                      type="button"
                      @click.stop.prevent="toggleColumnForTool(toolCall, column)"
                  >
                    {{ column }}
                  </button>
                </div>
                <div
                    v-if="selectedColumnsForTool(toolCall).length"
                    class="llm-column-actions"
                >
                  <button
                      class="llm-column-action"
                      type="button"
                      @click.stop.prevent="sendSelectedColumns(toolCall)"
                  >
                    Invia selezionate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="sending" class="llm-message-row llm-message-assistant">
        <div class="llm-message-bubble llm-progress" role="status" aria-live="polite">
          {{ progressMessage }}
        </div>
      </div>
    </div>

    <div v-if="error" class="llm-chat-error">{{ error }}</div>

    <form class="llm-chat-input-row" @submit.prevent="sendMessage()">
      <textarea
          ref="messageInput"
          v-model="draft"
          class="llm-chat-input"
          rows="2"
          placeholder="Message"
          :disabled="sending"
          @keydown.enter.exact.prevent="sendMessage()"
      ></textarea>
      <button class="llm-send-button" type="submit" :disabled="!canSend" aria-label="Send message" title="Send">
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="M3.5 10H15.5M11 5.5L15.5 10L11 14.5" />
        </svg>
      </button>
    </form>
  </section>
</template>

<script>
import LlmChatApiService from '@/services/LlmChatApiService';

export default {
  name: 'LlmChatWidget',
  emits: ['close'],

  data() {
    return {
      chatId: null,
      draft: '',
      sending: false,
      progressMessage: 'Sto preparando la richiesta…',
      progressRun: null,
      progressTimer: null,
      closing: false,
      messages: [],
      error: '',
      selectedColumnsByArtifact: {}
    };
  },

  computed: {
    canSend() {
      return this.draft.trim().length > 0 && !this.sending;
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.$refs.messageInput?.focus();
    });
  },

  beforeUnmount() {
    this.stopProgress();
    this.closeSession();
  },

  methods: {
    stopProgress() {
      this.progressRun = null;
      clearTimeout(this.progressTimer);
      this.progressTimer = null;
    },
    startProgress(id) {
      this.progressRun = id;
      const poll = async () => {
        if (this.progressRun !== id) return;
        try {
          const progress = await LlmChatApiService.getProgress(this.chatId);
          if (this.progressRun === id && progress.id === id && progress.message) {
            this.progressMessage = progress.message;
          }
        } catch (_) { /* Progress failure must not interrupt the actual request. */ }
        if (this.progressRun === id) this.progressTimer = setTimeout(poll, 900);
      };
      this.progressTimer = setTimeout(poll, 300);
    },
    async sendMessage(textOverride = null) {
      const text = (typeof textOverride === 'string' ? textOverride : this.draft).trim();
      if (!text || this.sending) return;

      this.error = '';
      this.draft = '';
      const userMessage = this.buildMessage('user', text);
      this.messages.push(userMessage);
      this.sending = true;
      this.progressMessage = 'Sto preparando la richiesta…';
      this.scrollToBottom();

      try {
        if (!this.chatId) {
          const chat = await LlmChatApiService.createChat({ firstUserMessage: text });
          this.chatId = chat.chat_id;
        }

        const progressId = userMessage.id;
        this.startProgress(progressId);
        const response = await LlmChatApiService.sendMessage(this.chatId, {
          progressId,
          message: text,
          history: this.chatHistory(userMessage.id),
          maxToolCalls: 4
        });

        const toolCalls = response.tool_calls || [];
        const fallbackMessage = toolCalls.length
            ? 'The tools completed, but the assistant did not generate a final text response.'
            : 'The assistant did not generate a response.';
        this.messages.push(this.buildMessage('assistant', response.message || fallbackMessage, toolCalls));
      } catch (error) {
        const message =
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Chat request failed';
        this.error = message;
        this.messages.push(this.buildMessage('assistant', message));
      } finally {
        this.stopProgress();
        this.sending = false;
        this.scrollToBottom();
      }
    },

    buildMessage(role, text, toolCalls = []) {
      return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        role,
        text,
        toolCalls
      };
    },

    chatHistory(currentUserMessageId) {
      return this.messages
          .filter(message => message.id !== currentUserMessageId)
          .filter(message => message.role === 'user' || message.role === 'assistant')
          .slice(-40)
          .map(message => ({
            role: message.role,
            text: message.text
          }));
    },

    formatToolName(toolName = '') {
      return String(toolName).replace(/_/g, ' ');
    },

    findDownloadUrl(value) {
      if (!value || typeof value !== 'object') return '';
      if (typeof value.download_url === 'string') return value.download_url;

      for (const child of Object.values(value)) {
        if (Array.isArray(child)) {
          for (const item of child) {
            const found = this.findDownloadUrl(item);
            if (found) return found;
          }
        } else if (child && typeof child === 'object') {
          const found = this.findDownloadUrl(child);
          if (found) return found;
        }
      }

      return '';
    },

    artifactForTool(toolCall) {
      const data = toolCall?.data;
      if (data && typeof data === 'object') {
        if (Array.isArray(data.columns) && data.columns.length) return data;
        if (data.artifact && Array.isArray(data.artifact.columns) && data.artifact.columns.length) {
          return data.artifact;
        }
      }
      return null;
    },

    artifactKeyForTool(toolCall) {
      const artifact = this.artifactForTool(toolCall);
      return artifact?.artifact_id || toolCall?.metadata?.artifact_id || toolCall?.tool_name || 'artifact';
    },

    columnOptionsForTool(toolCall) {
      const artifact = this.artifactForTool(toolCall);
      if (!artifact || !Array.isArray(artifact.columns)) return [];
      return artifact.columns
          .filter(column => typeof column === 'string' && column.trim() && !/tenant/i.test(column.replace(/[^a-z0-9]/gi, '')))
          .slice(0, 80);
    },

    shouldShowColumnPicker(toolCalls, index) {
      if (!Array.isArray(toolCalls) || !this.columnOptionsForTool(toolCalls[index]).length) {
        return false;
      }
      for (let i = toolCalls.length - 1; i > index; i -= 1) {
        if (this.columnOptionsForTool(toolCalls[i]).length) {
          return false;
        }
      }
      return true;
    },

    selectedColumnsForTool(toolCall) {
      return (this.selectedColumnsByArtifact[this.artifactKeyForTool(toolCall)] || []).filter(column => this.columnOptionsForTool(toolCall).includes(column));
    },

    isColumnSelected(toolCall, column) {
      return this.selectedColumnsForTool(toolCall).includes(column);
    },

    toggleColumnForTool(toolCall, column) {
      const key = this.artifactKeyForTool(toolCall);
      const selected = this.selectedColumnsForTool(toolCall);
      const next = selected.includes(column)
          ? selected.filter(value => value !== column)
          : [...selected, column];

      this.selectedColumnsByArtifact = {
        ...this.selectedColumnsByArtifact,
        [key]: next
      };
      this.draft = this.columnSelectionMessage(next, toolCall);
    },

    columnSelectionMessage(columns, toolCall) {
      if (!columns.length) return '';
      const artifact = this.artifactForTool(toolCall);
      if (!artifact?.artifact_id || !artifact?.workspace_id) return '';
      return `Esporta ora in CSV l'artifact ${artifact.artifact_id} nello workspace ${artifact.workspace_id}, usando esattamente queste colonne in questo ordine: ${columns.join(', ')}. Fornisci il pulsante di download del file esportato.`;
    },

    async sendSelectedColumns(toolCall) {
      const selected = this.selectedColumnsForTool(toolCall);
      if (!selected.length) return;
      await this.sendMessage(this.columnSelectionMessage(selected, toolCall));
    },

    async downloadToolArtifact(toolCall) {
      const downloadUrl = this.findDownloadUrl(toolCall);
      if (!downloadUrl) return;
      try {
        await LlmChatApiService.downloadArtifact(downloadUrl);
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Download failed';
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const scroller = this.$refs.messagesScroller;
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    },

    closePanel() {
      this.closeSession();
      this.$emit('close');
    },

    async closeSession() {
      if (!this.chatId || this.closing) return;
      this.closing = true;
      const chatId = this.chatId;
      this.chatId = null;
      try {
        await LlmChatApiService.closeChat(chatId);
      } catch (error) {
        console.error('Failed to close LLM chat:', error);
      }
    }
  }
};
</script>

<style scoped>
.llm-chat-panel {
  position: relative;
  z-index: 231;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto auto;
  min-height: 460px;
  height: min(72vh, 760px);
  overflow: visible;
  isolation: isolate;
  color: rgba(255, 255, 255, 0.96);
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  animation: llmSurfaceFloat 7s ease-in-out infinite;
}

.llm-chat-panel::before {
  content: '';
  position: absolute;
  inset: -10% -7% -8%;
  z-index: 0;
  border-radius: 48px;
  background:
    radial-gradient(circle at 14% 12%, rgba(56, 189, 248, 0.13), transparent 28%),
    radial-gradient(circle at 88% 24%, rgba(74, 222, 128, 0.10), transparent 27%),
    radial-gradient(circle at 58% 88%, rgba(99, 102, 241, 0.10), transparent 31%);
  filter: blur(18px);
  opacity: 0.9;
  pointer-events: none;
  animation: llmAmbientGlow 9s ease-in-out infinite;
}

.llm-chat-panel::after {
  content: '';
  position: absolute;
  inset: auto 0 72px;
  z-index: 0;
  height: 1px;
  pointer-events: none;
  background: linear-gradient(90deg, transparent, rgba(91, 182, 255, 0.26), rgba(74, 222, 128, 0.18), transparent);
  box-shadow: 0 0 22px rgba(91, 182, 255, 0.12);
}

.llm-chat-header,
.llm-chat-messages,
.llm-chat-error,
.llm-chat-input-row {
  position: relative;
  z-index: 1;
}

.llm-chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 0 18px;
  border-bottom: 0;
}

.llm-chat-title {
  font-size: 18px;
  font-weight: 720;
  line-height: 1.1;
  letter-spacing: 0.01em;
  color: rgba(255, 255, 255, 0.96);
  text-shadow: 0 0 22px rgba(91, 182, 255, 0.16);
}

.llm-chat-subtitle {
  max-width: 300px;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(91, 182, 255, 0.78);
}

.llm-icon-button,
.llm-send-button {
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: rgba(255, 255, 255, 0.035);
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  transition: color 0.16s ease, background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
}

.llm-icon-button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.llm-send-button {
  width: 42px;
  height: 42px;
  border-radius: 10px;
}

.llm-icon-button:hover,
.llm-send-button:hover:not(:disabled) {
  color: rgba(255, 255, 255, 0.96);
  background: rgba(91, 182, 255, 0.12);
  border-color: rgba(91, 182, 255, 0.28);
  transform: translateY(-1px);
}

.llm-icon-button svg,
.llm-send-button svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.llm-send-button:disabled {
  cursor: default;
  opacity: 0.38;
}

.llm-chat-messages {
  min-height: 0;
  overflow-y: auto;
  padding: 4px 2px 18px;
  scrollbar-width: thin;
  scrollbar-color: hsla(0, 0%, 100%, .16) transparent;
  mask-image: linear-gradient(to bottom, transparent 0, #000 20px, #000 calc(100% - 20px), transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 20px, #000 calc(100% - 20px), transparent 100%);
}

.llm-chat-empty {
  max-width: 860px;
  padding: 0 0 0 18px;
  border-left: 1px solid rgba(91, 182, 255, 0.42);
  background: transparent;
  color: rgba(255, 255, 255, 0.50);
  font-size: 13px;
  line-height: 1.65;
  animation: llmMessageInLeft 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.llm-message-row {
  display: flex;
  margin-bottom: 14px;
  will-change: transform, opacity;
}

.llm-message-user {
  justify-content: flex-end;
}

.llm-message-assistant {
  justify-content: flex-start;
}

.llm-message-bubble {
  position: relative;
  max-width: min(82%, 560px);
  padding: 12px 15px;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  background: rgba(255, 255, 255, 0.035);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.04),
    0 12px 28px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.llm-message-bubble::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  background: linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.10) 45%, transparent 70%);
  transform: translateX(-130%);
  animation: llmMessageSheen 760ms ease 90ms both;
}

.llm-message-user .llm-message-bubble {
  background: linear-gradient(135deg, rgba(91, 182, 255, 0.18), rgba(37, 99, 235, 0.10));
  border-color: rgba(91, 182, 255, 0.28);
  animation: llmMessageInRight 360ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.llm-message-assistant .llm-message-bubble {
  animation: llmMessageInLeft 390ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.llm-message-assistant .llm-message-bubble:not(.llm-thinking) {
  border-color: rgba(74, 222, 128, 0.14);
  background:
    linear-gradient(135deg, rgba(74, 222, 128, 0.07), rgba(56, 189, 248, 0.045)),
    rgba(255, 255, 255, 0.025);
}

.llm-message-text {
  position: relative;
  z-index: 1;
  white-space: pre-wrap;
  word-break: break-word;
  color: rgba(255, 255, 255, 0.86);
  font-size: 13px;
  line-height: 1.55;
}

.llm-tool-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}

.llm-tool-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.llm-tool-chip {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 7px;
  min-height: 28px;
  padding: 5px 7px;
  border-radius: 10px;
  background: rgba(91, 182, 255, 0.08);
  border: 1px solid rgba(91, 182, 255, 0.16);
}

.llm-tool-chip.failed {
  background: rgba(255, 91, 110, 0.08);
  border-color: rgba(255, 91, 110, 0.20);
}

.llm-tool-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #5bb6ff;
}

.llm-tool-chip.failed .llm-tool-dot {
  background: #ff5b6e;
}

.llm-tool-name,
.llm-tool-meta,
.llm-tool-download {
  min-width: 0;
  font-size: 10px;
  font-weight: 700;
}

.llm-tool-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.68);
}

.llm-tool-meta {
  color: rgba(255, 255, 255, 0.38);
}

.llm-tool-download {
  border: 0;
  padding: 3px 6px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
}

.llm-column-picker {
  padding: 8px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.035);
}

.llm-column-picker-header {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 7px;
  color: rgba(255, 255, 255, 0.48);
  font-size: 10px;
  font-weight: 700;
}

.llm-column-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  max-height: 92px;
  overflow-y: auto;
}

.llm-column-chip {
  min-width: 0;
  max-width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 7px;
  padding: 4px 7px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.055);
  color: rgba(255, 255, 255, 0.68);
  cursor: pointer;
  font-size: 10px;
  font-weight: 650;
}

.llm-column-chip.selected {
  border-color: rgba(91, 182, 255, 0.46);
  background: rgba(91, 182, 255, 0.16);
  color: rgba(255, 255, 255, 0.94);
}

.llm-column-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 7px;
}

.llm-column-action {
  border: 0;
  border-radius: 7px;
  padding: 5px 8px;
  background: rgba(91, 182, 255, 0.18);
  color: rgba(255, 255, 255, 0.88);
  cursor: pointer;
  font-size: 10px;
  font-weight: 750;
}

.llm-chat-error {
  margin: 0;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 91, 110, 0.20);
  background: rgba(255, 91, 110, 0.10);
  color: rgba(255, 255, 255, 0.82);
  font-size: 11px;
  line-height: 1.4;
}

.llm-progress {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.5;
}

.llm-thinking {
  display: inline-flex;
  gap: 5px;
  align-items: center;
  min-width: 52px;
}

.llm-thinking span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.46);
  animation: llmPulse 1s ease-in-out infinite;
}

.llm-thinking span:nth-child(2) {
  animation-delay: 0.14s;
}

.llm-thinking span:nth-child(3) {
  animation-delay: 0.28s;
}

.llm-chat-input-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  padding: 16px 0 0;
  border-top: 0;
}

.llm-chat-input {
  width: 100%;
  min-height: 42px;
  max-height: 128px;
  resize: vertical;
  border: 1px solid rgba(91, 182, 255, 0.18);
  border-radius: 14px;
  outline: none;
  padding: 11px 13px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.028));
  color: rgba(255, 255, 255, 0.88);
  font-size: 13px;
  line-height: 1.35;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 0 0 1px rgba(255, 255, 255, 0.015);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.llm-chat-input::placeholder {
  color: rgba(255, 255, 255, 0.32);
}

.llm-chat-input:focus {
  border-color: rgba(91, 182, 255, 0.42);
  background:
    linear-gradient(180deg, rgba(91, 182, 255, 0.075), rgba(255, 255, 255, 0.035));
  box-shadow:
    0 0 0 3px rgba(91, 182, 255, 0.08),
    0 0 28px rgba(91, 182, 255, 0.10);
}

@keyframes llmPulse {
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.35;
  }
  40% {
    transform: translateY(-2px);
    opacity: 0.9;
  }
}

@keyframes llmSurfaceFloat {
  0%, 100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-3px);
  }
}

@keyframes llmAmbientGlow {
  0%, 100% {
    opacity: 0.72;
    transform: scale(0.99);
  }

  50% {
    opacity: 1;
    transform: scale(1.02);
  }
}

@keyframes llmMessageInLeft {
  from {
    opacity: 0;
    transform: translateX(-18px) translateY(8px) scale(0.98);
    filter: blur(6px);
  }

  to {
    opacity: 1;
    transform: translateX(0) translateY(0) scale(1);
    filter: blur(0);
  }
}

@keyframes llmMessageInRight {
  from {
    opacity: 0;
    transform: translateX(18px) translateY(8px) scale(0.98);
    filter: blur(6px);
  }

  to {
    opacity: 1;
    transform: translateX(0) translateY(0) scale(1);
    filter: blur(0);
  }
}

@keyframes llmMessageSheen {
  0% {
    opacity: 0;
    transform: translateX(-130%);
  }

  35% {
    opacity: 0.55;
  }

  100% {
    opacity: 0;
    transform: translateX(130%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .llm-chat-panel,
  .llm-chat-panel::before,
  .llm-message-bubble,
  .llm-message-bubble::before,
  .llm-chat-empty {
    animation: none;
  }
}

@media (max-width: 1200px) {
  .llm-chat-panel {
    height: min(74vh, 720px);
  }
}

@media (max-width: 900px) {
  .llm-chat-panel {
    min-height: 420px;
    height: min(76vh, 680px);
  }
}

@media (max-width: 680px) {
  .llm-chat-header {
    padding: 0 0 14px;
  }

  .llm-chat-messages {
    padding: 4px 0 14px;
  }

  .llm-message-bubble {
    max-width: 90%;
  }

  .llm-chat-input-row {
    padding: 12px 0 0;
  }
}
</style>
